<template>
  <div id="brave-app" :class="[{ nowrap: nowrap() }]" :data-brand="siteBrand" :data-section="section">
    <component :is="headerComponent" :class="{ bare: bare() }" />

    <transition name="fade">
      <router-view :key="$route.fullPath" />
    </transition>

    <brave-modal
      v-for="({component, props, fullscreen}, index) in modals"
      :key="`${component.name || 'modal'}-${index}`"
      :component="component"
      :props="props"
      :fullscreen="!!fullscreen"
    />
    <shared-footer :class="{ collapse: bare() }" />
    <transition name="fade">
      <cookie-message
        v-if="showCookieMessage"
        @close="handleCloseCookieMessage"
        ref="cookieMessage"
      />
    </transition>
  </div>
</template>

<script>
import {mapMutations, mapState} from 'vuex'
import header from 'components/shared-header'
import siteBlocksHeader from 'components/siteblocks-header'
import footer from 'components/shared-footer'
import modal from 'brave-routing/components/brave-modal'
import cookieMessage from 'components/cookie-message'
import {SITEBLOCKS_HEADER_ROUTE_WHITELIST} from "../app-constants";
import {
  hasCookieDecision,
  readConsent,
  notifyConsentTags,
  reconcileConsentMirrors,
  LEGACY_CONSENT_KEY,
  CONSENT_KEY,
} from 'utils/cookie-consent'

export default {
  name: 'brave-app',
  data() {
    return {
      /* Stay false until we know localStorage + (for “needs banner”) window load — avoids mounting
       * the banner for users who already accepted, then tearing it down (flash). Original 10s delay
       * approximated “after load”; we gate on load instead. */
      showCookieMessage: false,
      _cookieRevealTimer: null,
      _cookieLoadListener: null,
    }
  },
  created () {
    reconcileConsentMirrors()
    /* Matches index.html inline guard so SPA remounts / edge cases still hide baked-in markup. */
    if (hasCookieDecision()) {
      try {
        document.documentElement.classList.add('bn-gdpr-consented')
      } catch (e) {
        /* ignore */
      }
    }
  },
  mounted() {
    var ua = window.navigator.userAgent;
    var msie = ua.indexOf("MSIE ");
    if (window.navigator.userAgent !== 'PRERENDER') {
      document.body.classList.add('enable-animations');
    }
    if (msie > 0 || !!navigator.userAgent.match(/Trident.*rv\:11\./)) {
      document.body.classList.add('is-ie')
    }

    window.addEventListener('storage', this._onGdprStorage)
    this._initCookieBannerAfterConsentCheck()
    if (!/PRERENDER/i.test(window.navigator.userAgent) && hasCookieDecision()) {
      notifyConsentTags(readConsent())
    }

    // console.log('route', this.$route);
    // console.log('Current Route', this.getCurrentRoute());
    // console.log('siteblocks_header_route_whitelist', SITEBLOCKS_HEADER_ROUTE_WHITELIST);
  },
  computed: {
    ...mapState([
      'modals'
    ]),
    headerComponent() {
      if (SITEBLOCKS_HEADER_ROUTE_WHITELIST.includes(this.getCurrentRoute())
          && (this.$route.query.brand === 'siteblocks' || this.$route.query.from === 'siteblocks')) {
        return 'siteblocks-header';
      }
      return 'shared-header';
    },
    siteBrand() {
      if (this.$route.query.brand === 'siteblocks' || this.$route.query.from === 'siteblocks') {
        return 'siteblocks';
      } else {
        return 'bravenet';
      }
    },
    section() {
      // Use getCurrentRoute() to get the modified path
      return this.getCurrentRoute();
    }
  },
  watch: {
    '$route'() {
      this.updateTitle()
    }
  },
  beforeDestroy () {
    window.removeEventListener('storage', this._onGdprStorage)
    this._clearCookieLoadListener()
    if (this._cookieRevealTimer != null) {
      clearTimeout(this._cookieRevealTimer)
      this._cookieRevealTimer = null
    }
  },
  methods: {
    _clearCookieLoadListener () {
      if (this._cookieLoadListener) {
        window.removeEventListener('load', this._cookieLoadListener)
        this._cookieLoadListener = null
      }
    },

    _initCookieBannerAfterConsentCheck () {
      /* Prerender has no consent cookie; after load we’d mount the banner and it gets baked into
       * static HTML. Returning users who already accepted then see a flash until client JS removes
       * it. Cookie UI is client-only — never emit it in the prerender snapshot. */
      if (/PRERENDER/i.test(window.navigator.userAgent)) {
        this.showCookieMessage = false
        return
      }

      if (hasCookieDecision()) {
        this.showCookieMessage = false
        return
      }

      const revealVisible = () => {
        this.$nextTick(() => {
          if (!this.showCookieMessage || !this.$refs.cookieMessage) return
          this._cookieRevealTimer = window.setTimeout(() => {
            this._cookieRevealTimer = null
            if (this.$refs.cookieMessage) {
              this.$refs.cookieMessage.hideCookie = false
            }
          }, 0)
        })
      }

      const onLoad = () => {
        this._clearCookieLoadListener()
        if (hasCookieDecision()) return
        this.showCookieMessage = true
        revealVisible()
      }

      this._cookieLoadListener = onLoad
      if (document.readyState === 'complete') {
        onLoad()
      } else {
        window.addEventListener('load', this._cookieLoadListener)
      }
    },

    _onGdprStorage (ev) {
      if (ev.key !== LEGACY_CONSENT_KEY && ev.key !== CONSENT_KEY) return
      reconcileConsentMirrors()
      if (hasCookieDecision()) {
        this.showCookieMessage = false
        this._clearCookieLoadListener()
        try {
          document.documentElement.classList.add('bn-gdpr-consented')
        } catch (e) {
          /* ignore */
        }
        notifyConsentTags(readConsent())
      }
    },
    getCurrentRoute() {
      let currentFullPath = this.$route.fullPath.slice(1); // Remove leading slash
      if (currentFullPath === '/' || currentFullPath === '') {
        currentFullPath = 'home';
      }
      // Split the path at the first question mark to remove query parameters
      const pathWithoutQuery = currentFullPath.split('?')[0].replace(/\/+$/, '');
      const modifiedPath = pathWithoutQuery.replace(/\//g, '-'); // Replace / with -
      // Check for the specific case 'sitebuilder-templates' and return 'sitebuilder'
      if (modifiedPath === 'sitebuilder-templates') {
        return 'sitebuilder';
      }
      return modifiedPath;
    },
    handleCloseCookieMessage() {
      this.showCookieMessage = false
      this._clearCookieLoadListener()
      if (this._cookieRevealTimer != null) {
        clearTimeout(this._cookieRevealTimer)
        this._cookieRevealTimer = null
      }
    },
    getCookie() {
      try {
        const c = readConsent()
        return c ? JSON.stringify(c) : null
      } catch (e) {
        return null
      }
    },
    ...mapMutations([
      'INCREMENT',
    ]),
    bare() {
      const barePaths = ['/register', '/login', '/lostpassword']
      // Apache DirectorySlash often serves /login/; barePaths only listed slashless paths.
      const path = this.$route.path.replace(/\/+$/, '') || '/'
      return barePaths.includes(path)
    },
    nowrap() {
      const noWrapPaths = [
        /\/register\?nowrap/,
        /\/login\?nowrap/,
        /\/lostpassword\?nowrap/
      ]
      const full = this.$route.fullPath
      const q = full.indexOf('?')
      const pathPart = (q === -1 ? full : full.slice(0, q)).replace(/\/+$/, '') || '/'
      const rest = q === -1 ? '' : full.slice(q)
      const normalized = pathPart + rest
      return noWrapPaths.some(rx => rx.test(normalized))
    },
    updateTitle() {
      document.title = this.$route.meta.title
      document.querySelector('meta[name="description"]').setAttribute("content", this.$route.meta.description)
      document.querySelector('meta[name="keywords"]').setAttribute("content", this.$route.meta.keywords)

    },
  },
  components: {
    'cookie-message': cookieMessage,
    'shared-header': header,
    'siteblocks-header': siteBlocksHeader,
    'shared-footer': footer,
    'brave-modal': modal
  },
}
</script>

<style>

.fade-enter-active, .fade-leave-active {
  transition-property: opacity;
  transition-duration: .2s;
}

.fade-enter-active {
  transition-delay: .2s;
}

.fade-enter, .fade-leave-active {
  opacity: 0
}

[v-cloak] {
  display: none;
}
.hide-cookie {
  display: block;
  visibility: hidden;
}
</style>